import { cloneElement, useState, useRef, useEffect } from 'react'
import { useSpring, animated } from '@react-spring/web'

import usePrefersReducedMotion from '@/hooks/prefers-reduced-motion'

// Utils
import { getUniqueId } from '@/utils/id'

// Icons
import Chevron from '@/assets/svg/chevron.svg'

function Collapsible({
  children,
  trigger,
  isTriggerBelowContent,
  isFullWidth,
  defaultVisibility,
  ...rest
}) {
  const [isVisible, setIsVisible] = useState(defaultVisibility)
  const [id] = useState(() => getUniqueId('collapsible'))
  const contentRef = useRef(null)

  const prefersReducedMotion = usePrefersReducedMotion()

  const contentHeight =
    contentRef?.current?.getBoundingClientRect().height || 100
  const expand = useSpring({
    config: { tension: 350 },
    height: isVisible ? contentHeight : 0,
    immediate: prefersReducedMotion,
  })
  const rotation = useSpring({
    config: { tension: 350 },
    transform: isVisible ? 'rotate(180deg)' : 'rotate(0deg)',
    immediate: prefersReducedMotion,
  })

  useEffect(() => {
    setIsVisible(defaultVisibility)
  }, [defaultVisibility])

  // Control whether to mark content as `visibility: hidden` so that child nodes
  // cannot be focused via keyboard.
  const [isHidden, setIsHidden] = useState(!isVisible)

  useEffect(() => {
    if (isVisible && isHidden) {
      setIsHidden(false)
    } else if (!isVisible && !isHidden) {
      setTimeout(() => {
        setIsHidden(true)
      }, 350)
    }
  }, [isVisible, isHidden])

  const triggerElement = trigger
    ? cloneElement(
        trigger,
        {
          onClick: () => {
            setIsVisible((prev) => !prev)
          },
          'aria-controls': id,
          'aria-expanded': isVisible,
        },
        [
          trigger.props.children,
          <animated.div
            key={id}
            style={rotation}
            className={`origin-center text-dark ${
              isFullWidth ? 'ml-auto' : 'ml-5'
            }`}
          >
            <Chevron className="h-12 transform" />
          </animated.div>,
        ]
      )
    : null

  return (
    <div {...rest}>
      {!isTriggerBelowContent ? triggerElement : null}
      <animated.div
        id={id}
        style={expand}
        aria-hidden={!isVisible}
        className="overflow-hidden"
      >
        <div ref={contentRef} className={isHidden ? 'invisible' : undefined}>
          {children}
        </div>
      </animated.div>
      {isTriggerBelowContent ? triggerElement : null}
    </div>
  )
}

export default Collapsible
